import React, { useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Avatar,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import PublicIcon from '@mui/icons-material/Public';
import PeopleIcon from '@mui/icons-material/People';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { useNavigate } from 'react-router-dom';

// Animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(0, 119, 181, 0.4);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(0, 119, 181, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 119, 181, 0);
  }
`;

// Styled Components
const PageSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  position: 'relative',
}));


interface AnimatedBoxProps {
  delay?: number;
  children: React.ReactNode;
}

const AnimatedBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'delay',
})<AnimatedBoxProps>(({ delay = 0 }) => ({
  animation: `${fadeIn} 1s ease forwards ${delay}s`,
  opacity: 0,
  position: 'relative',
  zIndex: 3,
  willChange: 'opacity, transform'
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  transition: 'transform 0.3s, box-shadow 0.3s',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 15px 35px rgba(0, 0, 0, 0.1)',
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: 70,
  height: 70,
  borderRadius: '50%',
  backgroundColor: 'rgba(0, 119, 181, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  color: '#0077B5',
  transition: 'all 0.3s ease',
  '&:hover': {
    animation: `${pulseAnimation} 1.5s infinite`,
  },
}));

const JobCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.05)',
  border: '2px dashed rgba(0, 119, 181, 0.3)',
  transition: 'transform 0.3s, box-shadow 0.3s, border 0.3s',
  overflow: 'hidden',
  padding: theme.spacing(1),
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.1)',
    border: '2px dashed rgba(0, 119, 181, 0.6)',
  },
}));

const TestimonialCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(4),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.05)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -10,
    left: 30,
    width: 20,
    height: 20,
    backgroundColor: 'white',
    transform: 'rotate(45deg)',
    boxShadow: '-5px -5px 5px rgba(0, 0, 0, 0.03)',
    zIndex: -1,
  },
}));

const CircularPattern = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '300px',
  height: '300px',
  borderRadius: '50%',
  border: '2px dashed rgba(0, 119, 181, 0.2)',
  opacity: 0.5,
  zIndex: 0,
}));


// Create decorative elements for page sections
const WavePattern = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: '120px',
  background: 'url("/images/wave-pattern.svg")',
  backgroundSize: 'cover',
  opacity: 0.1,
  transform: 'rotate(180deg)',
  zIndex: 0,
}));

const BlobPattern = styled(Box)(({ theme }) => ({
  position: 'absolute',
  right: '-150px',
  top: '30%',
  width: '350px',
  height: '350px',
  background: 'url("/images/blob-pattern.svg")',
  backgroundSize: 'contain',
  backgroundRepeat: 'no-repeat',
  opacity: 0.15,
  zIndex: 0,
}));


const ImageWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
  transition: 'transform 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-10px)',
  },
}));

// Define missing components
const SectionTitle = styled(Typography)({
  fontWeight: 700,
  color: '#0077B5',
});

const GrowthCard = styled(Card)({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '16px',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  transition: 'transform 0.3s, box-shadow 0.3s',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 15px 35px rgba(0, 0, 0, 0.1)',
  },
});

// BoxGrid: Simple grid-like layout using Box
const BoxGrid: React.FC<{
  children: React.ReactNode;
  spacing?: number;
}> = ({ children, spacing = 3 }) => {
  return (
    <Box sx={{ 
      display: 'flex', 
      flexWrap: 'wrap', 
      margin: -spacing / 2,
      width: `calc(100% + ${spacing}px)`,
    }}>
      {children}
    </Box>
  );
};

// BoxGridItem: Column in the grid
const BoxGridItem: React.FC<{
  children: React.ReactNode;
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
}> = ({ children, xs = 12, sm, md, lg }) => {
  const getWidth = () => {
    const widths: Record<string, string> = {};
    if (xs) widths.xs = `${(xs / 12) * 100}%`;
    if (sm) widths.sm = `${(sm / 12) * 100}%`;
    if (md) widths.md = `${(md / 12) * 100}%`;
    if (lg) widths.lg = `${(lg / 12) * 100}%`;
    return widths;
  };

  return (
    <Box sx={{ 
      padding: 1.5,
      width: getWidth(),
      boxSizing: 'border-box',
    }}>
      {children}
    </Box>
  );
};

interface JobOpening {
  id: string;
  title: string;
  location: string;
  type: string;
  description: string;
  requirements: string[];
}

// Main Component
const Careers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // Add ref for scrolling to open roles section
  const openRolesRef = useRef<HTMLDivElement>(null);

  // Job openings will be managed by HR through the admin panel
  const jobOpenings: JobOpening[] = [];

  // Testimonials data
  const testimonials = [
    {
      id: 1,
      quote: "VerTechie gave me the chance to grow as a developer while working on meaningful projects that actually make a difference in the IT hiring landscape.",
      author: "Anil Kumar",
      position: "Senior Developer",
      avatar: "/images/testimonial-1.jpg"
    },
    {
      id: 2,
      quote: "The supportive culture at VerTechie makes it a great place to build your career. I've learned more here in a year than in my previous three years combined.",
      author: "Priya Sharma",
      position: "Product Manager",
      avatar: "/images/testimonial-2.jpg"
    },
  ];


  // Career growth section with better visuals
  const CareerGrowthSection = () => (
    <PageSection sx={{ position: 'relative', overflow: 'hidden', bgcolor: '#f0f7ff' }}>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: { xs: '150px', md: '250px' },
          height: '100%',
          background: 'url("/images/growth-pattern.svg")',
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right center',
          opacity: 0.1,
          zIndex: 0,
        }}
      />
      <Container>
        <Typography variant="h3" component="h2" align="center" sx={{ 
          fontWeight: 700,
          mb: 6,
          color: '#0077B5', 
          fontSize: { xs: '2rem', md: '2.5rem' }
        }}>
          Career Growth at VerTechie
        </Typography>
        
        <BoxGrid spacing={4}>
          <BoxGridItem xs={12} md={3}>
            <GrowthCard>
              <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                px: 2,
                py: 3
              }}>
                <Box sx={{ 
                  mb: 2,
                  width: '80px', 
                  height: '80px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  bgcolor: 'rgba(0, 119, 181, 0.1)',
                  color: '#0077B5'
                }}>
                  <Box sx={{ fontSize: '2.5rem' }}>🌱</Box>
                </Box>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Continuous Learning
                </Typography>
                <Typography>
                  Access unlimited learning resources to keep your skills sharp and stay on the cutting edge.
                </Typography>
              </Box>
            </GrowthCard>
          </BoxGridItem>
          
          <BoxGridItem xs={12} md={3}>
            <GrowthCard>
              <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                px: 2,
                py: 3
              }}>
                <Box sx={{ 
                  mb: 2,
                  width: '80px', 
                  height: '80px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  bgcolor: 'rgba(0, 119, 181, 0.1)',
                  color: '#0077B5'
                }}>
                  <Box sx={{ fontSize: '2.5rem' }}>👥</Box>
                </Box>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Mentorship
                </Typography>
                <Typography>
                  Get paired with experienced team members who will help guide your development.
                </Typography>
              </Box>
            </GrowthCard>
          </BoxGridItem>
          
          <BoxGridItem xs={12} md={3}>
            <GrowthCard>
              <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                px: 2,
                py: 3
              }}>
                <Box sx={{ 
                  mb: 2,
                  width: '80px', 
                  height: '80px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  bgcolor: 'rgba(0, 119, 181, 0.1)',
                  color: '#0077B5'
                }}>
                  <Box sx={{ fontSize: '2.5rem' }}>🚀</Box>
                </Box>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Career Advancement
                </Typography>
                <Typography>
                  Clear paths for promotion and opportunities to lead meaningful projects.
                </Typography>
              </Box>
            </GrowthCard>
          </BoxGridItem>
          
          <BoxGridItem xs={12} md={3}>
            <GrowthCard>
              <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                px: 2,
                py: 3
              }}>
                <Box sx={{ 
                  mb: 2,
                  width: '80px', 
                  height: '80px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  bgcolor: 'rgba(0, 119, 181, 0.1)',
                  color: '#0077B5'
                }}>
                  <Box sx={{ fontSize: '2.5rem' }}>⚖️</Box>
                </Box>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Work-Life Balance
                </Typography>
                <Typography>
                  Flexible schedules and policies that respect your personal time and well-being.
                </Typography>
              </Box>
            </GrowthCard>
          </BoxGridItem>
        </BoxGrid>
      </Container>
    </PageSection>
  );



  return (
    <Box sx={{ overflow: 'hidden' }}>


      {/* Why Join VerTechie Section */}
      <PageSection sx={{ bgcolor: 'white', position: 'relative', overflow: 'hidden' }}>
        <BlobPattern />
        <Container maxWidth="lg">
          <Box sx={{ position: 'relative' }}>
            <CircularPattern sx={{ 
              top: '-150px', 
              left: '-150px', 
              width: '300px', 
              height: '300px' 
            }} />
            
            <AnimatedBox delay={0.3} sx={{ textAlign: 'center', mb: 6 }}>
              <Typography 
                variant="h3" 
                component="h2" 
                sx={{ 
                  fontWeight: 700, 
                  mb: 2,
                  color: '#0077B5',
                  fontSize: { xs: '2rem', md: '2.5rem' }
                }}
              >
                Why Join VerTechie?
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  maxWidth: 800, 
                  mx: 'auto',
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  color: 'text.secondary',
                  lineHeight: 1.7
                }}
              >
                At VerTechie, we're building a platform that ensures trust in IT hiring and learning. 
                Join us to make an impact in a market plagued by fake profiles, connecting verified 
                professionals with genuine opportunities. Here's why VerTechie is the place for you:
              </Typography>
            </AnimatedBox>

            <BoxGrid spacing={4}>
              <BoxGridItem xs={12} sm={6} md={3}>
                <AnimatedBox delay={0.4}>
                  <FeatureCard>
                    <CardContent sx={{ p: 3 }}>
                      <IconContainer>
                        <RocketLaunchIcon fontSize="large" />
                      </IconContainer>
                      <Typography variant="h5" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
                        Mission-Driven Work
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Be part of a team dedicated to authenticity and quality in IT hiring.
                        Your work directly impacts the careers of thousands of professionals.
                      </Typography>
                    </CardContent>
                  </FeatureCard>
                </AnimatedBox>
              </BoxGridItem>

              <BoxGridItem xs={12} sm={6} md={3}>
                <AnimatedBox delay={0.5}>
                  <FeatureCard>
                    <CardContent sx={{ p: 3 }}>
                      <IconContainer>
                        <LightbulbIcon fontSize="large" />
                      </IconContainer>
                      <Typography variant="h5" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
                        Innovative Environment
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Work with cutting-edge tech to solve real-world problems. 
                        We embrace creative solutions and encourage experimentation.
                      </Typography>
                    </CardContent>
                  </FeatureCard>
                </AnimatedBox>
              </BoxGridItem>

              <BoxGridItem xs={12} sm={6} md={3}>
                <AnimatedBox delay={0.6}>
                  <FeatureCard>
                    <CardContent sx={{ p: 3 }}>
                      <IconContainer>
                        <PublicIcon fontSize="large" />
                      </IconContainer>
                      <Typography variant="h5" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
                        Global Impact
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Join a platform trusted by professionals and companies in 50+ countries.
                        Help shape the future of IT hiring on a global scale.
                      </Typography>
                    </CardContent>
                  </FeatureCard>
                </AnimatedBox>
              </BoxGridItem>

              <BoxGridItem xs={12} sm={6} md={3}>
                <AnimatedBox delay={0.7}>
                  <FeatureCard>
                    <CardContent sx={{ p: 3 }}>
                      <IconContainer>
                        <PeopleIcon fontSize="large" />
                      </IconContainer>
                      <Typography variant="h5" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
                        Supportive Culture
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Thrive in a collaborative, inclusive workplace that values your growth.
                        We prioritize work-life balance and personal development.
                      </Typography>
                    </CardContent>
                  </FeatureCard>
                </AnimatedBox>
              </BoxGridItem>
            </BoxGrid>
          </Box>
        </Container>
      </PageSection>

      {/* Career Growth Section */}
      <CareerGrowthSection />


      {/* Founder's Message Section */}
      <PageSection sx={{ bgcolor: '#f8f9fa', position: 'relative', overflow: 'hidden' }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            position: 'relative',
            zIndex: 1,
            textAlign: 'center',
            maxWidth: '900px',
            mx: 'auto'
          }}>
            <Avatar
              src="/images/founder.jpg"
              alt="Sai Krishna Reddy"
              sx={{ 
                width: 120, 
                height: 120,
                mx: 'auto',
                mb: 3,
                border: '4px solid white',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
              }}
            />
            
            <Typography 
              variant="h4" 
              component="h2" 
              sx={{ 
                fontWeight: 700,
                mb: 2,
                color: '#0077B5',
                fontSize: { xs: '1.75rem', md: '2.25rem' }
              }}
            >
              A Message from Our Founder & CEO
            </Typography>
            
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 4,
                color: 'text.secondary',
                fontWeight: 500
              }}
            >
              Sai Krishna Reddy
            </Typography>
            
            <Box sx={{ 
              position: 'relative',
              p: 4,
              borderRadius: 2,
              bgcolor: 'white',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #0077B5, #00BFFF)',
                borderRadius: '2px'
              }
            }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  lineHeight: 1.8,
                  color: 'text.primary',
                  mb: 3,
                  fontStyle: 'italic'
                }}
              >
                "At VerTechie, we're not just building another tech platform – we're revolutionizing how the world verifies and connects IT professionals. Our vision is bold: to create a global ecosystem where every professional's skills and achievements are transparent, verifiable, and valued.
              </Typography>
              
              <Typography 
                variant="body1" 
                sx={{ 
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  lineHeight: 1.8,
                  color: 'text.primary',
                  mb: 3,
                  fontStyle: 'italic'
                }}
              >
                Success for us means transforming the IT hiring landscape, eliminating fake profiles, and ensuring that genuine talent gets the recognition it deserves. We're committed to building a platform that not only verifies credentials but also fosters meaningful connections between professionals and opportunities.
              </Typography>
              
              <Typography 
                variant="body1" 
                sx={{ 
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  lineHeight: 1.8,
                  color: 'text.primary',
                  fontStyle: 'italic'
                }}
              >
                Join us in this mission to make the digital world more trustworthy. Together, we can create a future where verification is seamless, opportunities are genuine, and success is measured by the impact we make on people's careers and lives."
              </Typography>
            </Box>
          </Box>
        </Container>
      </PageSection>
    </Box>
  );
};

export default Careers; 